import React, { useState, useRef, useCallback } from 'react';
import { Button, Input, Card, Badge, message, Typography, Space, Result } from 'antd';
import {
  CameraOutlined,
  CheckCircleFilled,
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import useAuth from '../hooks/useAuth';
import api from '../config/api';

const { Title, Text } = Typography;

const DEPARTMENTS = [
  { code: '01', name: 'Hogar', emoji: '🏠', odooCategId: 64 },
  { code: '02', name: 'Mercería', emoji: '🧵', odooCategId: 65 },
  { code: '03', name: 'Fiestas', emoji: '🎉', odooCategId: 67 },
  { code: '04', name: 'Papelería', emoji: '📚', odooCategId: 69 },
  { code: '05', name: 'Juguetes', emoji: '🎮', odooCategId: 68 },
  { code: '06', name: 'Manualidades', emoji: '🎨', odooCategId: 70 },
  { code: '07', name: 'Ropa', emoji: '👗', odooCategId: 71 },
  { code: '08', name: 'Belleza', emoji: '💄', odooCategId: 72 },
  { code: '09', name: 'Electrónica', emoji: '🔌', odooCategId: 73 },
  { code: '10', name: 'Mascotas', emoji: '🐾', odooCategId: 74 },
  { code: '11', name: 'Temporada', emoji: '🎃', odooCategId: 66 },
  { code: '12', name: 'No sé', emoji: '❓', odooCategId: 63 },
];

function compressImage(file, maxWidth = 1200) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const PhotoButton = ({ label, required, photo, onCapture }) => {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const compressed = await compressImage(file);
      onCapture(compressed);
    }
    e.target.value = '';
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Button
        block
        size="large"
        onClick={() => inputRef.current?.click()}
        style={{
          height: 80,
          borderColor: photo ? '#52c41a' : required ? '#E8192C' : '#d9d9d9',
          borderWidth: 2,
          background: photo ? '#f6ffed' : '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {photo ? (
          <CheckCircleFilled style={{ fontSize: 24, color: '#52c41a' }} />
        ) : (
          <CameraOutlined style={{ fontSize: 24, color: required ? '#E8192C' : '#999' }} />
        )}
        <span style={{ fontSize: 12 }}>{label}{required ? ' *' : ''}</span>
      </Button>
      {photo && (
        <img
          src={photo}
          alt={label}
          style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 4, marginTop: 4 }}
        />
      )}
    </div>
  );
};

const ProductCapture = () => {
  const { user } = useAuth();
  const [department, setDepartment] = useState(null);
  const [barcode, setBarcode] = useState('');
  const [productName, setProductName] = useState('');
  const [photoFront, setPhotoFront] = useState(null);
  const [photoBarcode, setPhotoBarcode] = useState(null);
  const [photoBack, setPhotoBack] = useState(null);
  const [saving, setSaving] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const canSave = productName.trim() && photoFront && photoBarcode && department;

  const resetForm = useCallback(() => {
    setBarcode('');
    setProductName('');
    setPhotoFront(null);
    setPhotoBarcode(null);
    setPhotoBack(null);
  }, []);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post('/products', {
        barcode,
        name: productName.trim(),
        department: {
          code: department.code,
          name: department.name,
          odooCategId: department.odooCategId,
        },
        photoFront,
        photoBarcode,
        photoBack: photoBack || '',
      });
      setTodayCount(c => c + 1);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
      }, 1200);
    } catch (err) {
      message.error(err.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Success animation
  if (showSuccess) {
    return (
      <div style={{ padding: 16, textAlign: 'center', paddingTop: 80 }}>
        <Result
          status="success"
          title="Producto guardado"
          subTitle="Listo para el siguiente"
        />
      </div>
    );
  }

  // Department selection
  if (!department) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, padding: '8px 0',
        }}>
          <Title level={4} style={{ margin: 0 }}>Captura de Productos</Title>
          <Badge count={todayCount} showZero>
            <Text strong>{user?.name?.split(' ')[0] || 'Usuario'}</Text>
          </Badge>
        </div>

        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Selecciona el departamento del producto:
        </Text>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
        }}>
          {DEPARTMENTS.map(dept => (
            <Card
              key={dept.code}
              hoverable
              onClick={() => setDepartment(dept)}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
              }}
              bodyStyle={{ padding: 12 }}
            >
              <div style={{ fontSize: 32 }}>{dept.emoji}</div>
              <div style={{ fontSize: 12, marginTop: 4, fontWeight: 500 }}>{dept.name}</div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Capture form
  return (
    <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => { setDepartment(null); resetForm(); }}
        >
          Cambiar depto
        </Button>
        <Badge count={todayCount} showZero>
          <Text strong>{user?.name?.split(' ')[0]}</Text>
        </Badge>
      </div>

      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderColor: '#E8192C',
          borderWidth: 2,
          textAlign: 'center',
        }}
      >
        <span style={{ fontSize: 24 }}>{department.emoji}</span>
        <Text strong style={{ marginLeft: 8, fontSize: 16 }}>{department.name}</Text>
      </Card>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Código de barras</Text>
          <Input
            size="large"
            inputMode="numeric"
            placeholder="Escanea o escribe el código"
            value={barcode}
            onChange={e => setBarcode(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 18 }}
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Nombre del producto *</Text>
          <Input
            size="large"
            placeholder="Como dice la etiqueta"
            value={productName}
            onChange={e => setProductName(e.target.value)}
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Fotos del producto</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <PhotoButton label="FRENTE" required photo={photoFront} onCapture={setPhotoFront} />
            <PhotoButton label="BARCODE" required photo={photoBarcode} onCapture={setPhotoBarcode} />
            <PhotoButton label="ATRÁS" required={false} photo={photoBack} onCapture={setPhotoBack} />
          </div>
        </div>

        <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
          <Text style={{ fontSize: 12 }}>
            📸 Fondo blanco · Buena luz · Sin flash · Que se lea el código de barras
          </Text>
        </Card>

        <Button
          type="primary"
          size="large"
          block
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!canSave}
          onClick={handleSave}
          style={{
            height: 56,
            fontSize: 18,
            fontWeight: 'bold',
            background: canSave ? '#E8192C' : undefined,
            borderColor: canSave ? '#E8192C' : undefined,
          }}
        >
          GUARDAR → SIGUIENTE
        </Button>
      </Space>
    </div>
  );
};

export default ProductCapture;
